import React from 'react'

import Button from 'components/common/Button'
import Text from 'components/common/Text'
import Modal, {
  ModalHeader,
  ModalCloseButton,
  ModalTitle,
  ModalSubtitle
} from 'components/Modal'
import theme from 'theme'

const ModalFooterSection: React.FC<{
  cancelText?: string | boolean
  cancelButtonProps?: Record<string, any>
  confirmText?: string | boolean
  confirmButtonProps?: Record<string, any>
  cancelButtonType?: string
  confirmButtonType?: string
  loading?: boolean
  onCancel?: (event, options?) => void
  onConfirm?: () => void
  disabled?: boolean
  confirmationContent?: React.ReactNode
  footerDescription?: React.ReactNode
  hideFooterSeparator?: boolean
}> = ({
  cancelText = 'Cancel',
  cancelButtonProps,
  confirmText = 'Yes',
  confirmButtonProps,
  cancelButtonType = 'secondary',
  confirmButtonType = 'destructive',
  loading = false,
  onCancel = () => undefined,
  disabled = false,
  onConfirm = () => undefined,
  footerDescription = null,
  hideFooterSeparator = false
}) => {
  return (
    <>
      {!hideFooterSeparator && (
        <div
          css={{
            marginTop: theme.space[3],
            marginBottom: theme.space[3],
            height: 1,
            backgroundColor: theme.colors.gray[1]
          }}
        />
      )}
      {footerDescription}

      <section
        css={{
          display: 'grid',
          gap: theme.space[3],
          gridTemplateColumns: cancelText === false ? '1fr' : '1fr 1fr'
        }}
      >
        {cancelText !== false && (
          <Button
            data-change-state-button={cancelText}
            disabled={disabled}
            onClick={onCancel}
            {...{ [cancelButtonType]: true }}
            {...cancelButtonProps}
          >
            {cancelText}
          </Button>
        )}
        {confirmText !== false && (
          <Button
            data-change-confirm-button={confirmText}
            disabled={disabled}
            loading={loading}
            onClick={onConfirm}
            {...{ [confirmButtonType]: true }}
            {...confirmButtonProps}
          >
            {confirmText}
          </Button>
        )}
      </section>
    </>
  )
}

export { ModalFooterSection }

const ConfirmationModal: React.FC<{
  open?: boolean
  disabled?: boolean
  title?: React.ReactNode
  subtitle?: React.ReactNode
  message?: Record<string, any> | React.ReactNode
  cancelText?: string | boolean
  confirmText?: string
  onCancel?: (event, options) => void
  onConfirm?: () => void
  className?: string
  hideFooter?: boolean
  hideOverlay?: boolean
  shouldCloseOnOverlayClick?: boolean
  footerDescription?: React.ReactNode
  confirmButtonType?: string
  loading?: boolean
}> = ({
  open,
  disabled,
  title,
  subtitle,
  message,
  cancelText,
  confirmText,
  onCancel = () => undefined,
  onConfirm,
  className,
  hideFooter,
  hideOverlay = false,
  shouldCloseOnOverlayClick = true,
  footerDescription,
  confirmButtonType,
  loading
}) => {
  return (
    <Modal
      data-test-confirmation-modal-name
      className={className}
      css={{ width: '32em' }}
      isOpen={open}
      shouldCloseOnOverlayClick={shouldCloseOnOverlayClick}
      style={{
        overlay: {
          backgroundColor: hideOverlay ? 'transparent' : 'rgba(0,0,0,0.3)'
        }
      }}
      onRequestClose={onCancel}
    >
      <ModalHeader>
        <ModalCloseButton
          onClick={(e) => onCancel(e, { discardModal: true })}
        />
        <ModalTitle>{title}</ModalTitle>
        {subtitle && <ModalSubtitle>{subtitle}</ModalSubtitle>}
      </ModalHeader>
      <div
        css={{
          marginTop: subtitle ? 0 : theme.space[3],
          textAlign: 'center'
        }}
      >
        {typeof message === 'string' ? (
          <Text
            as="p"
            css={{
              textAlign: 'center'
            }}
          >
            {message}
          </Text>
        ) : (
          message
        )}
      </div>
      {!hideFooter && (
        <ModalFooterSection
          cancelText={cancelText}
          confirmButtonType={confirmButtonType}
          confirmText={confirmText}
          disabled={disabled}
          footerDescription={footerDescription}
          loading={loading}
          onCancel={onCancel}
          onConfirm={onConfirm}
        />
      )}
    </Modal>
  )
}

export default ConfirmationModal
